import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import { getApiErrorMessage } from "../../lib/apiError";
import {
  contentPages,
  downloadContent,
  type ContentArticle,
} from "../../lib/contentApi";
import { useContentDraftGuard } from "../../hooks/useContentDraftGuard";

interface Block {
  id: string;
  block_type: string;
  level?: number;
  content: string;
  data?: { alt?: string; src?: string };
}
interface Preview extends ContentArticle {
  html: string;
  markdown: string;
  content_blocks: Block[];
}
interface Revision {
  id: number;
  label: string;
  created_at: string;
  snapshot: {
    title: string;
    meta_title: string;
    meta_description: string;
    keywords: string;
    blocks: Array<{ text: string; block_type: string }>;
  };
}
interface Snippet {
  id: number;
  name: string;
  category: string;
  content: string;
  approved: boolean;
}
const emptySnippet = {
  name: "",
  category: "cta",
  content: "",
  approved: false,
};

export function ArticleTools({ articleId }: { articleId: number }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("seo");
  const [width, setWidth] = useState("desktop");
  const [draft, setDraft] = useState<Partial<ContentArticle>>({});
  const [revisionId, setRevisionId] = useState<number | null>(null);
  const [restore, setRestore] = useState(false);
  const [snippet, setSnippet] = useState<Partial<Snippet>>(emptySnippet);
  const [snippetDirty, setSnippetDirty] = useState(false);
  useContentDraftGuard(Object.keys(draft).length > 0 || snippetDirty);
  const preview = useQuery({
    queryKey: ["article-preview", articleId],
    queryFn: async () =>
      (await api.get<Preview>(`/api/marketing/articles/${articleId}/preview/`))
        .data,
  });
  const revisions = useQuery({
    queryKey: ["article-revisions", articleId],
    queryFn: () =>
      contentPages<Revision>(`/api/marketing/articles/${articleId}/revisions/`),
  });
  const snippets = useQuery({
    queryKey: ["content-snippets"],
    queryFn: () => contentPages<Snippet>("/api/marketing/content-snippets/"),
  });
  const pages = useQuery({
    queryKey: ["content-link-pages"],
    queryFn: () =>
      contentPages<{ id: number; url: string; keyword_1: string }>(
        "/api/top-pages/",
      ),
  });
  const invalidate = () => {
    [
      "article",
      "article-preview",
      "content-blocks",
      "article-revisions",
    ].forEach((key) => void qc.invalidateQueries({ queryKey: [key] }));
  };
  const save = useMutation({
    mutationFn: () => api.patch(`/api/marketing/articles/${articleId}/`, draft),
    onSuccess: () => {
      setDraft({});
      invalidate();
      toast.success("SEO settings saved");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not save SEO settings")),
  });
  const version = useMutation({
    mutationFn: (restoring: boolean) =>
      api.post(
        `/api/marketing/articles/${articleId}/${restoring ? "restore-revision" : "revisions"}/`,
        restoring ? { revision_id: revisionId } : {},
      ),
    onSuccess: () => {
      invalidate();
      setRestore(false);
      toast.success("Article version updated");
    },
    onError: (error) =>
      toast.error(
        getApiErrorMessage(error, "Could not update the article version"),
      ),
  });
  const saveSnippet = useMutation({
    mutationFn: () =>
      snippet.id
        ? api.patch(`/api/marketing/content-snippets/${snippet.id}/`, snippet)
        : api.post("/api/marketing/content-snippets/", snippet),
    onSuccess: () => {
      setSnippet(emptySnippet);
      setSnippetDirty(false);
      void qc.invalidateQueries({ queryKey: ["content-snippets"] });
      toast.success("Reusable block saved");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not save reusable block")),
  });
  const insert = useMutation({
    mutationFn: (entry: Snippet) =>
      api.post("/api/marketing/content-blocks/", {
        article: articleId,
        content: entry.content,
        block_type: entry.category === "cta" ? "cta" : "p",
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Block inserted at the end of the article");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not insert block")),
  });
  const article = preview.data;
  if (preview.isPending) return <Typography>Loading article tools…</Typography>;
  if (!article || preview.isError)
    return (
      <Alert color="danger">
        Could not load article tools.
        <Button onClick={() => void preview.refetch()}>Retry</Button>
      </Alert>
    );
  const field = (key: "meta_title" | "meta_description" | "keywords") =>
    draft[key] ?? article[key] ?? "";
  const headings = article.content_blocks.filter(
    (block) => block.block_type === "h",
  );
  const missingAlt = article.content_blocks.filter(
    (block) => block.block_type === "img" && !block.data?.alt?.trim(),
  );
  const revision = revisions.data?.find((entry) => entry.id === revisionId);
  const currentText = [
    article.title,
    article.meta_title,
    article.meta_description,
    ...article.content_blocks.map((block) => block.content),
  ].join("\n\n");
  const versionText = revision
    ? [
        revision.snapshot.title,
        revision.snapshot.meta_title,
        revision.snapshot.meta_description,
        ...revision.snapshot.blocks.map((block) => block.text),
      ].join("\n\n")
    : "";
  const slug =
    article.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .slice(0, 80) || "article";
  const seoChecks = [
    [
      headings.filter((block) => block.level === 1).length === 1,
      "One primary heading",
    ],
    [
      !headings.some(
        (block, i) =>
          i > 0 && (block.level || 2) > (headings[i - 1].level || 2) + 1,
      ),
      "Heading levels follow a clear sequence",
    ],
    [
      missingAlt.length === 0,
      `${missingAlt.length} content images need alternative text`,
    ],
    [Boolean(field("meta_title").trim()), "Search title is set"],
    [Boolean(field("meta_description").trim()), "Search description is set"],
  ];
  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <Tabs value={tab} onChange={(_, value) => setTab(String(value))}>
        <TabList sx={{ flexWrap: "wrap" }}>
          <Tab value="seo">SEO workbench</Tab>
          <Tab value="preview">Preview & export</Tab>
          <Tab value="revisions">Versions</Tab>
          <Tab value="snippets">Reusable blocks</Tab>
        </TabList>
        <TabPanel value="seo">
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>
                Search title · {field("meta_title").length}/60
              </FormLabel>
              <Input
                value={field("meta_title")}
                onChange={(e) =>
                  setDraft({ ...draft, meta_title: e.target.value })
                }
                slotProps={{ input: { maxLength: 60 } }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                Search description · {field("meta_description").length}/160
              </FormLabel>
              <Textarea
                minRows={2}
                value={field("meta_description")}
                onChange={(e) =>
                  setDraft({ ...draft, meta_description: e.target.value })
                }
                slotProps={{ textarea: { maxLength: 160 } }}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Target keywords</FormLabel>
              <Input
                value={field("keywords")}
                onChange={(e) =>
                  setDraft({ ...draft, keywords: e.target.value })
                }
              />
            </FormControl>
            <Card variant="soft">
              <Typography level="body-xs">
                Search-result preview · appearance can vary
              </Typography>
              <Typography level="title-lg" sx={{ color: "primary.600" }}>
                {field("meta_title") || article.title}
              </Typography>
              <Typography>
                {field("meta_description") ||
                  "Add a helpful description of this article."}
              </Typography>
            </Card>
            <Stack spacing={0.5}>
              {seoChecks.map(([passed, label]) => (
                <Typography
                  key={String(label)}
                  color={passed ? "success" : "warning"}
                >
                  {passed ? "✓" : "○"} {String(label)}
                </Typography>
              ))}
            </Stack>
            {missingAlt.map((block) => (
              <ImageAltField key={block.id} block={block} onSave={invalidate} />
            ))}
            <Typography level="title-sm">
              Relevant pages to consider linking
            </Typography>
            {pages.isError && (
              <Alert color="warning">
                Website page suggestions could not load.
              </Alert>
            )}
            {pages.data
              ?.filter((page) => /^https?:\/\//i.test(page.url))
              .slice()
              .sort(
                (a, b) =>
                  Number(
                    Boolean(
                      b.keyword_1 &&
                        article.keywords
                          ?.toLowerCase()
                          .includes(b.keyword_1.toLowerCase()),
                    ),
                  ) -
                  Number(
                    Boolean(
                      a.keyword_1 &&
                        article.keywords
                          ?.toLowerCase()
                          .includes(a.keyword_1.toLowerCase()),
                    ),
                  ),
              )
              .slice(0, 5)
              .map((page) => (
                <Stack
                  key={page.id}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <Typography sx={{ flex: 1, overflowWrap: "anywhere" }}>
                    {page.keyword_1 || page.url}
                  </Typography>
                  <Button
                    size="sm"
                    variant="outlined"
                    onClick={() =>
                      navigator.clipboard
                        .writeText(page.url)
                        .then(() => toast.success("Page link copied"))
                        .catch(() => toast.error("Could not copy link"))
                    }
                  >
                    Copy link
                  </Button>
                </Stack>
              ))}
            <Typography level="body-xs">
              These checks are editorial suggestions, not a ranking score.
            </Typography>
            <Button
              loading={save.isPending}
              disabled={!Object.keys(draft).length}
              onClick={() => save.mutate()}
            >
              Save SEO settings
            </Button>
          </Stack>
        </TabPanel>
        <TabPanel value="preview">
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              <Select
                aria-label="Preview size"
                value={width}
                onChange={(_, value) => setWidth(value || "desktop")}
              >
                <Option value="desktop">Desktop</Option>
                <Option value="mobile">Mobile</Option>
              </Select>
              <Button variant="outlined" onClick={() => void preview.refetch()}>
                Refresh saved preview
              </Button>
              <Button
                onClick={() =>
                  downloadContent(article.html, `${slug}.html`, "text/html")
                }
              >
                Download HTML
              </Button>
              <Button
                onClick={() =>
                  downloadContent(
                    article.markdown,
                    `${slug}.md`,
                    "text/markdown",
                  )
                }
              >
                Download Markdown
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  navigator.clipboard
                    .writeText(article.markdown)
                    .then(() => toast.success("Article copied"))
                    .catch(() => toast.error("Could not copy article"))
                }
              >
                Copy article
              </Button>
            </Stack>
            <Typography level="body-sm">
              Preview reflects saved content. Save your current block before
              exporting.
            </Typography>
            <Box
              sx={{
                width: width === "mobile" ? 390 : "100%",
                maxWidth: "100%",
                mx: "auto",
                bgcolor: "white",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "md",
                overflow: "hidden",
              }}
            >
              <iframe
                title="Saved article preview"
                sandbox=""
                srcDoc={article.html}
                style={{ width: "100%", height: 600, border: 0 }}
              />
            </Box>
          </Stack>
        </TabPanel>
        <TabPanel value="revisions">
          <Stack spacing={2}>
            <Typography level="body-sm">
              Save a version to preserve the article, SEO settings, and all
              blocks. Restoring also saves the current version.
            </Typography>
            <Button
              loading={version.isPending}
              onClick={() => version.mutate(false)}
            >
              Save current version
            </Button>
            {revisions.isError && (
              <Alert color="danger">
                Versions could not load.
                <Button onClick={() => void revisions.refetch()}>Retry</Button>
              </Alert>
            )}
            <Select
              placeholder="Choose a saved version"
              value={revisionId}
              onChange={(_, value) => setRevisionId(value)}
            >
              {revisions.data?.map((entry) => (
                <Option key={entry.id} value={entry.id}>
                  {new Date(entry.created_at).toLocaleString()} · {entry.label}
                </Option>
              ))}
            </Select>
            {revision && (
              <>
                <Typography>
                  {versionText === currentText
                    ? "This version matches the current text."
                    : "This version differs from the current text."}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <Card>
                    <Typography level="title-sm">Selected version</Typography>
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        maxHeight: 400,
                        overflow: "auto",
                      }}
                    >
                      {versionText}
                    </Typography>
                  </Card>
                  <Card>
                    <Typography level="title-sm">Current version</Typography>
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        maxHeight: 400,
                        overflow: "auto",
                      }}
                    >
                      {currentText}
                    </Typography>
                  </Card>
                </Box>
                <Button color="warning" onClick={() => setRestore(true)}>
                  Restore selected version
                </Button>
              </>
            )}
            {!revisions.isPending && !revisions.data?.length && (
              <Alert>No saved versions yet.</Alert>
            )}
          </Stack>
        </TabPanel>
        <TabPanel value="snippets">
          <Stack spacing={2}>
            <Typography level="body-sm">
              Save reusable copy for your organization. Mark reviewed copy as
              approved before insertion.
            </Typography>
            {snippets.isError && (
              <Alert color="danger">Reusable blocks could not load.</Alert>
            )}
            {snippets.data?.map((entry) => (
              <Card key={entry.id}>
                <Typography level="title-md">
                  {entry.name} · {entry.approved ? "Approved" : "Draft"}
                </Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {entry.content}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="sm"
                    disabled={!entry.approved || insert.isPending}
                    onClick={() => insert.mutate(entry)}
                  >
                    Insert
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    onClick={() => {
                      if (
                        !snippetDirty ||
                        window.confirm("Discard the reusable block draft?")
                      ) {
                        setSnippet(entry);
                        setSnippetDirty(false);
                      }
                    }}
                  >
                    Edit
                  </Button>
                </Stack>
              </Card>
            ))}
            <FormControl>
              <FormLabel>Block name</FormLabel>
              <Input
                value={snippet.name}
                onChange={(e) => {
                  setSnippet({ ...snippet, name: e.target.value });
                  setSnippetDirty(true);
                }}
              />
            </FormControl>
            <Select
              aria-label="Block category"
              value={snippet.category}
              onChange={(_, value) => {
                setSnippet({ ...snippet, category: value || "cta" });
                setSnippetDirty(true);
              }}
            >
              {["cta", "service", "faq", "disclaimer"].map((value) => (
                <Option key={value} value={value}>
                  {value.toUpperCase()}
                </Option>
              ))}
            </Select>
            <Textarea
              aria-label="Reusable block content"
              minRows={3}
              value={snippet.content}
              onChange={(e) => {
                setSnippet({ ...snippet, content: e.target.value });
                setSnippetDirty(true);
              }}
            />
            <Checkbox
              label="I have reviewed and approved this copy for reuse"
              checked={Boolean(snippet.approved)}
              onChange={(e) => {
                setSnippet({ ...snippet, approved: e.target.checked });
                setSnippetDirty(true);
              }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                loading={saveSnippet.isPending}
                disabled={!snippet.name?.trim() || !snippet.content?.trim()}
                onClick={() => saveSnippet.mutate()}
              >
                Save reusable block
              </Button>
              <Button
                variant="plain"
                onClick={() => {
                  if (!snippetDirty || window.confirm("Discard this draft?")) {
                    setSnippet(emptySnippet);
                    setSnippetDirty(false);
                  }
                }}
              >
                New block
              </Button>
            </Stack>
          </Stack>
        </TabPanel>
      </Tabs>
      <Modal open={restore} onClose={() => setRestore(false)}>
        <ModalDialog>
          <Typography level="h4">Restore this version?</Typography>
          <Typography>
            Your current article will be saved as a version before replacement.
          </Typography>
          <Button
            color="warning"
            loading={version.isPending}
            onClick={() => version.mutate(true)}
          >
            Restore
          </Button>
          <Button variant="plain" onClick={() => setRestore(false)}>
            Cancel
          </Button>
        </ModalDialog>
      </Modal>
    </Card>
  );
}
function ImageAltField({
  block,
  onSave,
}: {
  block: Block;
  onSave: () => void;
}) {
  const [alt, setAlt] = useState(block.data?.alt || "");
  const mutation = useMutation({
    mutationFn: () =>
      api.patch(`/api/marketing/content-blocks/${block.id}/`, {
        data: { ...block.data, alt },
      }),
    onSuccess: onSave,
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not save alternative text")),
  });
  return (
    <FormControl>
      <FormLabel>Describe image: {block.content.slice(0, 70)}</FormLabel>
      <Stack direction="row" spacing={1}>
        <Input
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button loading={mutation.isPending} onClick={() => mutation.mutate()}>
          Save alt text
        </Button>
      </Stack>
    </FormControl>
  );
}
